/** Tipos do domínio do Portfólio e mapeamento do contrato da API (ADR-0010 / DEC-039). */

import { type PagedResult, readApiETag } from '@/features/pipeline/types';
import { parseDecimalPtBr } from '@/lib/domain/format';

export type { PagedResult };
export { readApiETag };

/** Status do investimento como vem da API. */
export type InvestmentApiStatus = 'Active' | 'Divested';

/** InvestmentListItem da API (GET /api/investments). Nomes resolvidos no backend. */
export interface ApiInvestmentListItem {
  id: string;
  dealId: string;
  codename: string;
  targetCompanyId: string;
  companyName: string | null;
  primaryThesisId: number | null;
  primaryThesisName: string | null;
  fundId: number;
  fundName: string | null;
  checkAmountBRL: number | null;
  moicExpected: number | null;
  moicRealized: number | null;
  irrExpected: number | null;
  irrRealized: number | null;
  investmentStatus: string;
  leadUserId: string;
  leadUserName: string | null;
  investmentDateUtc: string;
  exitDateUtc: string | null;
  eTag?: string;
  etag?: string;
}

/** Rodada de aporte (follow-on) — Portfólio v2 (DEC-040). */
export interface ApiInvestmentRound {
  id: string;
  amountBRL: number;
  deployedAtUtc: string;
  roundType: string;
  createdAtUtc: string;
}

/** InvestmentResponse da ficha (GET /api/investments/{id}). */
export interface ApiInvestmentResponse extends ApiInvestmentListItem {
  /** Split do cheque (breakdown informativo). Só na ficha, não na listagem. */
  checkDiscretionaryBRL: number | null;
  checkCoinvestmentBRL: number | null;
  teamUserIds: string[];
  teamUserNames: string[];
  tags: string[];
  rounds: ApiInvestmentRound[];
}

/** Rótulo do tipo de rodada para a ficha. */
export const ROUND_TYPE_LABEL: Record<string, string> = {
  Initial: 'Inicial',
  FollowOn: 'Follow-on',
};

/** Investimento normalizado para o domínio do frontend. */
export interface Investment {
  id: string;
  dealId: string;
  codename: string;
  companyName: string;
  thesisName: string | null;
  fundId: number;
  fundName: string;
  checkAmountBRL: number | null;
  moicExpected: number | null;
  moicRealized: number | null;
  irrExpected: number | null;
  irrRealized: number | null;
  status: InvestmentApiStatus;
  leadUserId: string;
  leadUserName: string;
  investmentDateUtc: string;
  exitDateUtc: string | null;
  etag: string;
}

export function toInvestment(raw: ApiInvestmentListItem): Investment {
  return {
    id: raw.id,
    dealId: raw.dealId,
    codename: raw.codename,
    companyName: raw.companyName ?? '(empresa sem nome)',
    thesisName: raw.primaryThesisName,
    fundId: raw.fundId,
    fundName: raw.fundName ?? '—',
    checkAmountBRL: raw.checkAmountBRL,
    moicExpected: raw.moicExpected,
    moicRealized: raw.moicRealized,
    irrExpected: raw.irrExpected,
    irrRealized: raw.irrRealized,
    status: raw.investmentStatus === 'Divested' ? 'Divested' : 'Active',
    leadUserId: raw.leadUserId,
    leadUserName: raw.leadUserName ?? '—',
    investmentDateUtc: raw.investmentDateUtc,
    exitDateUtc: raw.exitDateUtc,
    etag: readApiETag(raw),
  };
}

/** Achado F15: reexporta a fonte única (lib/domain/format.ts) sem tocar quem já importa daqui. */
export const parseDecimalInput = parseDecimalPtBr;

/** MOIC como múltiplo (ex.: 2,1x); travessão quando nulo. */
export function fmtMoic(value: number | null | undefined): string {
  return value != null ? `${value.toFixed(1).replace('.', ',')}x` : '—';
}

/** TIR (fração 0–1) como percentual inteiro (ex.: 22%); travessão quando nulo. */
export function fmtIrr(value: number | null | undefined): string {
  return value != null ? `${Math.round(value * 100)}%` : '—';
}

export const STATUS_LABEL: Record<InvestmentApiStatus, string> = {
  Active: 'Ativo',
  Divested: 'Desinvestido',
};
